import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "@/components/controls/Modal";
import { Button } from "@/components/ui/button";
import Text from "@/components/ui/text";
import FormNew from "@/components/controls/Form/FormNew";
import { FormSectionProps } from "@/components/controls/Form/FormSection";
import { FormFieldNewType } from "@/components/controls/Form/types";
import { OnboardingStage } from "@/pages/settings/components/contractsSettings/types";
import { StagePosition } from "@/pages/settings/components/shared/stageStudio/types";
import ColorPickerField from "./ColorPickerField";
import { DEFAULT_ONBOARDING_STAGE_COLOR } from "../colors";
import { NewOnboardingStageFormData } from "../types";
import { getPositionOptions } from "../utils";

const TRANSLATION_KEY = "settings.contracts.onboardingStudio.createStage";

interface Props {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  existingStages: OnboardingStage[];
  onCreate: (data: NewOnboardingStageFormData) => void;
}

const CreateOnboardingStageModal: React.FC<Props> = ({
  isOpen,
  setIsOpen,
  existingStages,
  onCreate
}) => {
  const { t } = useTranslation();
  const formRef = useRef<HTMLFormElement>(null);
  const [isFormValid, setIsFormValid] = useState(false);
  const [formSetValue, setFormSetValue] = useState<any>(null);
  const [color, setColor] = useState<string>(DEFAULT_ONBOARDING_STAGE_COLOR);

  useEffect(() => {
    if (!isOpen) {
      setIsFormValid(false);
      setColor(DEFAULT_ONBOARDING_STAGE_COLOR);
    }
  }, [isOpen]);

  const positionOptions = useMemo(
    () => getPositionOptions(existingStages, t),
    [existingStages, t]
  );

  useEffect(() => {
    if (typeof formSetValue === "function") {
      formSetValue("positionInPipeline", StagePosition.LAST, {
        shouldValidate: true
      });
    }
  }, [formSetValue]);

  const formFields: FormFieldNewType[] = [
    {
      name: "name",
      type: "text",
      required: true,
      label: t(`${TRANSLATION_KEY}.name`),
      layout: "flex",
      fieldClassName: "flex-1",
      fieldActionProps: {
        placeholder: t(`${TRANSLATION_KEY}.namePlaceholder`)
      }
    },
    {
      name: "color",
      type: "render",
      required: false,
      label: t(`${TRANSLATION_KEY}.color`),
      layout: "flex",
      fieldClassName: "w-[80px]",
      renderNode: <ColorPickerField value={color} onChange={setColor} />
    },
    {
      name: "description",
      type: "textarea",
      required: false,
      label: t(`${TRANSLATION_KEY}.description`),
      fieldActionProps: {
        placeholder: t(`${TRANSLATION_KEY}.descriptionPlaceholder`),
        minRows: 1
      }
    },
    {
      name: "positionInPipeline",
      type: "singleSelect",
      required: true,
      label: t(`${TRANSLATION_KEY}.position`),
      defaultValue: StagePosition.LAST,
      fieldActionProps: {
        options: positionOptions,
        placeholder: t(`${TRANSLATION_KEY}.selectPosition`),
        variant: "form",
        clearable: false
      }
    }
  ];

  const formSections: FormSectionProps[] = [
    {
      id: "name-and-color",
      fields: formFields.slice(0, 2),
      fieldsContainerClassName: "flex flex-row gap-6 items-end"
    },
    {
      id: "additional",
      fields: formFields.slice(2)
    }
  ];

  const handleFormValidation = React.useCallback((isValid: boolean) => {
    setIsFormValid(isValid);
  }, []);

  const handleSetValue = (formMethods: { setValue: any; getValues: any }) => {
    if (typeof formMethods.setValue === "function") {
      setFormSetValue(() => formMethods.setValue);
    }
  };

  const handleSubmit = (data: NewOnboardingStageFormData) => {
    onCreate({ ...data, color });
    setIsOpen(false);
  };

  return (
    <Modal
      open={isOpen}
      setIsOpen={setIsOpen}
      title={<Text variant="lgSubtitle">{t(`${TRANSLATION_KEY}.title`)}</Text>}
      content={
        <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
          <FormNew
            formRef={formRef}
            sections={formSections}
            isFormValid={handleFormValidation}
            onSubmit={handleSubmit}
            onSetValue={handleSetValue}
          />
        </div>
      }
      footer={
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={() => setIsOpen(false)}>
            {t(`${TRANSLATION_KEY}.cancel`)}
          </Button>
          <Button
            variant="primary"
            onClick={() => formRef.current?.requestSubmit()}
            disabled={!isFormValid}
            data-test="onboarding-create-stage-submit"
          >
            {t(`${TRANSLATION_KEY}.createStage`)}
          </Button>
        </div>
      }
      contentClassname="w-[640px] max-w-[640px]"
    />
  );
};

export default CreateOnboardingStageModal;
